import { SyllableContext } from "../pattern/checkSyllableContext";
import { Pattern } from "../pattern/pattern";
import { isPatternParsingError, patternAsString, PatternParsingError, patternParsingErrorToString, stringAsPattern } from "../pattern/serializePattern";
import { stringToSyllableContext, syllableContextToString, SyllableError } from "../pattern/serializeSyllableContext";
import { AddLengthRule } from "./lengthRules";
import { FeatureAttribute, Rule } from "./rule";
import { isComment, RuleType } from "./ruleSet";

/**
 * convert a rule to a string, e.g., `a > b / #_V`
 * returns `undefined` if serialization isn't supported for the given rule
 */
export function ruleToString(rule: RuleType): string | undefined {
  if (isComment(rule)) {
    return rule.isBlankLine ? ''
      : rule.comment !== '' ? `${commentPrefix} ${rule.comment}`
        : commentPrefix;
  }
  const basic = `${getRuleIpa(rule.from)} > ${getRuleIpa(rule.to)}`;
  const context = ruleContextToString(rule);
  if (context === undefined) {
    return undefined; // unsupported
  }
  const post = rule.post === 'swapWithNext' ? ` ${swapNext}`
    : rule.post === 'swapWithPrev' ? ` ${swapPrev}` : '';
  return `${basic}${context}${post}`;
}

/** convert a rule's context (prev, next, syllable) to a simple string or `undefined` if unsupported */
export function ruleContextToString(rule: Rule): string | undefined {
  const previous = contextToString(rule.previous);
  const next = contextToString(rule.next);
  if (previous === undefined || next === undefined) {
    return undefined; // unsupported
  }
  const prevNext = !rule.previous && !rule.next ? '' : ` ${previous}_${next}`;
  const syllable = syllablesToString(rule.syllable);
  const context = !rule.previous && !rule.next && !rule.syllable
    ? '' : ` /${prevNext}${syllable}`;
  return context;
}

const commentPrefix = '//';
const swapNext = '*(next)';
const swapPrev = '*(prev)';

function getRuleIpa(s: string) { return s === '' ? '∅' : s }

function contextToString(option: FeatureAttribute[] | Pattern<string> | undefined): string | undefined {
  if (option === undefined) {
    return '';
  }

  if (Array.isArray(option)) {
    const f = option[0];
    if (f === '#word') {
      return '#';
    } else if (option.length === 1) {
      return f === 'vowel' ? 'V' : f === 'consonant' ? 'C' : `[+${f}]`;
    } else {  // a feature array is basically an AND
      return patternAsString({ and: option.map(f => `:${f}`) });
    }
  }

  if (typeof option === 'string') {
    return option;
  }

  return patternAsString(option);
}

function syllablesToString(syllable?: SyllableContext): string | undefined {
  if (syllable === undefined) {
    return '';
  }
  const s = syllableContextToString(syllable);
  return ` '${s}'`;
}


/** parse a string into a rule */
export function stringToRule(s: string): RuleType | RuleParsingError {
  if (s.trim() === '') {
    return { comment: '', isBlankLine: true };
  } else if (s.startsWith(commentPrefix)) {
    const comment = s.substring(s[2] === ' ' ? 3 : 2);
    return { comment };
  }

  const pieces = getPieces(s);
  if (!('from' in pieces)) {
    return pieces;
  }

  const previous = stringToContext(pieces.previous);
  const next = stringToContext(pieces.next);
  if (isPatternParsingError(previous)) { return { patternError: previous }; }
  if (isPatternParsingError(next)) { return { patternError: next }; }
  const post = pieces.post === 'next' ? 'swapWithNext'
    : pieces.post === 'prev' ? 'swapWithPrev' : undefined;

  if (pieces.syllables) {
    const syllable = stringToSyllableContext(pieces.syllables)
    if ('error' in syllable) {
      return { syllableError: syllable };
    }
    const rule: Rule = { from: pieces.from, to: pieces.to, previous, next, syllable, post };
    return withoutUndefined(rule);
  }
  const rule: Rule = { from: pieces.from, to: pieces.to, previous, next, post };
  return withoutUndefined(rule);
}

function withoutUndefined(rule: Rule): Rule {
  const record: Record<string, any> = {};
  Object.getOwnPropertyNames(rule).forEach(k => {
    const v = (rule as Record<string, any>)[k];
    if (v !== undefined) record[k] = v;
  });
  return record as Rule;
}

/** parse the shortform of an AddLengthRule */
export function stringToLengthAdds(s: string): AddLengthRule | string {
  const pieces = getContext(s, 0);
  if (!('context' in pieces)) {
    return JSON.stringify(pieces);
  }

  // first piece is length in milliseconds
  const msStr = s.substring(0, pieces.context - 1).trim();
  const addMs = Number.parseInt(msStr);

  // parse previous & next
  const previous1 = stringToContext(pieces.previous);
  if (isPatternParsingError(previous1)) { return patternParsingErrorToString(previous1); }
  const previous = Array.isArray(previous1) ? previous1[0] : previous1;
  const next1 = stringToContext(pieces.next);
  if (isPatternParsingError(next1)) { return patternParsingErrorToString(next1); }
  const next = Array.isArray(next1) ? next1[0] : next1;

  // parse syllable
  if (pieces.syllables) {
    const syllable = stringToSyllableContext(pieces.syllables)
    if ('error' in syllable) {
      return JSON.stringify(syllable);
    }
    return { addMs, previous, next, syllable };
  } else {
    return { addMs, previous, next };
  }
}

export interface RuleParsingError {
  readonly missing?: '>' | '_';
  readonly syllableError?: SyllableError;
  readonly patternError?: PatternParsingError;
}
export function isRuleParsingError(check: unknown): check is RuleParsingError {
  const test = check as RuleParsingError;
  return test && (test.missing !== undefined || test.syllableError !== undefined || test.patternError !== undefined);
}
export function ruleParsingErrorToString(error: RuleParsingError): string {
  let s = '';
  if (error.missing) {
    s = s.concat(`missing ${error.missing}`);
  }
  if (error.syllableError) {
    s = s.concat(`${error.syllableError.error}: ${error.syllableError.unknown}`);
  }
  if (error.patternError) {
    s = s.concat(`${patternParsingErrorToString(error.patternError)}`);
  }
  return s;
}

interface RulePieces {
  readonly from: string;
  readonly to: string;
  readonly previous?: string;
  readonly next?: string;
  readonly syllables?: string;
  readonly post?: string;
}

/** e.g., `from > to / previous_next 'syllables' *(post)` */
function getPieces(s: string): RulePieces | RuleParsingError {
  // get positions
  const into = s.indexOf('>');
  if (into === -1) {
    return { missing: '>' };
  }

  // context: prev, next, syllables, post
  const result = getContext(s, into);
  if (!('context' in result)) {
    return result;
  }
  const { context, startPost, previous, next, syllables, post } = result;

  // from & to
  const fromRaw = s.substring(0, into).trim();
  const toRaw = context !== -1 ? s.substring(into + 1, context).trim()
    : startPost !== -1 ? s.substring(into + 1, startPost).trim()
      : s.substring(into + 1).trim();
  const from = fromRaw === '∅' ? '' : fromRaw;
  const to = toRaw === '∅' ? '' : toRaw;
  if (context === -1 && startPost === -1) {
    return { from, to };
  }

  return { from, to, previous, next, syllables, post };
}

/** pull apart the optional pieces of rule context */
function getContext(
  s: string, start: number
): { context: number, startPost: number, previous?: string, next?: string, syllables?: string, post?: string } | RuleParsingError {
  const context = s.indexOf('/', start);
  const testAnchor = context === -1 ? -1 : s.indexOf('_', context);
  const endSyllable = context === -1 ? -1 : s.lastIndexOf("'");
  const startSyllable = endSyllable === -1 ? -1 : s.lastIndexOf("'", endSyllable - 1);
  const anchor = testAnchor === -1 || startSyllable === -1 ? testAnchor
    : startSyllable < testAnchor ? -1 : testAnchor;
  if (context !== -1 && anchor === -1 && startSyllable === -1) {
    return { missing: '_' };
  }
  const startPost = s.lastIndexOf(' *(');
  const endPost = s.lastIndexOf(')');

  // previous & next & syllables & post
  const previous = anchor === -1 ? undefined : s.substring(context + 1, anchor).trim();
  const next = anchor === -1 ? undefined
    : startSyllable === -1 && startPost === -1 ? s.substring(anchor + 1).trim()
      : startPost === -1 ? s.substring(anchor + 1, startSyllable).trim()
        : s.substring(anchor + 1, startPost).trim();
  const syllables = startSyllable === -1 ? undefined : s.substring(startSyllable + 1, endSyllable);
  const post = startPost === -1 || endPost === -1 || endPost < startPost ? undefined
    : s.substring(startPost + 3, endPost);

  return { context, startPost, previous, next, syllables, post };
}

function stringToContext(s?: string): FeatureAttribute[] | Pattern<string> | PatternParsingError | undefined {
  if (s === undefined || s === '') {
    return undefined;
  } else if (s[0] === '[') {
    const a = s.replace(/\+|\]|\[/g, '');
    return a.split(' ') as FeatureAttribute[];
  } else {
    // stringAsPattern differs by returning ipa as /ipa/,
    // features with a leading :, and not returning FeatureAttribute[]
    const p = stringAsPattern(s);
    if (p !== undefined && typeof p === 'string') {
      if (p[0] === '/') {
        return p.substring(1, p.length - 1);
      } else if (p === '#word') {
        return ['#word'];
      } else if (p[0] === ':') {
        return [p.substring(1) as FeatureAttribute];
      }
    }
    return p ?? s;
  }
}
